import apiResponse from '@stores/ApiResponse';
import { QuestionView, ResponseView, SectionView } from '@views';
import { emptySection } from '@views/DashboardView';
import { Question } from '@models/Question';
import { Response } from '@models/Response';
import { Section, SurveyView } from '@models/Survey';
import { useQuery } from '@tanstack/react-query';
import { SurveyStore } from '@app/api/SurveyStore';
import { DueQuestionnaireView, QuestionnaireSittingStore } from '@app/api/QuestionnaireSittingStore';

export enum SurveyType {
  Onboarding = 'ONBOARDING',
  FinishLine = 'FINISH_LINE',
  TeamCelebration = 'TEAM_CELEBRATION',
}

/** Where the drawer's section came from, which decides how its answers are saved. */
export enum DrawerSectionSource {
  Survey = 'SURVEY',
  Questionnaire = 'QUESTIONNAIRE',
}

export interface NextSurveySectionResponse {
  source: DrawerSectionSource;
  section: SectionView;
  // Set for a survey section.
  surveyType: SurveyType | null;
  surveyId: number | null;
  // Set for a questionnaire sitting.
  sittingId: number | null;
}

/** Reads the next section one source has for the user; a section with no questions means nothing is due. */
export interface DrawerSectionReader {
  read(cohortId: number): Promise<NextSurveySectionResponse>;
}

// A copy of the section with every answer cleared, the shape the drawer records answers onto.
export const toDrawerSection = (initialSection: Section): SectionView =>
  ({
    ...initialSection,
    description: '',
    questions: initialSection.questions.map(
      (question: Question) =>
        ({
          ...question,
          userResponse: '',

          responses: question.responses.map(
            (response: Response) =>
              ({
                ...response,
                selected: false,
                textResponse: '',
              }) as ResponseView
          ),
        }) as QuestionView
    ),
  }) as SectionView;

export class SurveySectionReader implements DrawerSectionReader {
  constructor(private readonly surveyStore: SurveyStore = new SurveyStore()) {}

  async read(cohortId: number): Promise<NextSurveySectionResponse> {
    const survey = await apiResponse.handleApiResponse<SurveyView>(() =>
      this.surveyStore.GetNextAvailableSurveyForCohort(cohortId)
    );
    return {
      source: DrawerSectionSource.Survey,
      section: toDrawerSection(survey.sections[0] ?? emptySection),
      surveyType: survey.surveyType,
      surveyId: survey.surveyId,
      sittingId: null,
    };
  }
}

export class QuestionnaireSectionReader implements DrawerSectionReader {
  constructor(
    private readonly questionnaireSittingStore: QuestionnaireSittingStore = new QuestionnaireSittingStore()
  ) {}

  async read(cohortId: number): Promise<NextSurveySectionResponse> {
    const due = await apiResponse.handleApiResponse<DueQuestionnaireView>(() =>
      this.questionnaireSittingStore.GetDue(cohortId)
    );
    return {
      source: DrawerSectionSource.Questionnaire,
      section: toDrawerSection(due.sections[0] ?? emptySection),
      surveyType: null,
      surveyId: null,
      sittingId: due.sittingId,
    };
  }
}

/**
 * The next section the drawer should show, from the first source that has one due.
 * Surveys go first, so Getting to Know You is answered before the baseline questionnaire on
 * day 1. A source is only asked once the ones before it have nothing due, which matters for
 * questionnaires: asking for the due sitting records it as served.
 */
export class NextSurveySectionViewCreator {
  constructor(
    private readonly readers: DrawerSectionReader[] = [new SurveySectionReader(), new QuestionnaireSectionReader()]
  ) {}

  async createView(cohortId: number): Promise<NextSurveySectionResponse> {
    for (const reader of this.readers) {
      const view = await reader.read(cohortId);
      if (view.section.questions.length > 0) return view;
    }
    return {
      source: DrawerSectionSource.Survey,
      section: emptySection,
      surveyType: null,
      surveyId: null,
      sittingId: null,
    };
  }
}

export const useNextSurveySectionViewCreator = (groupId: number) =>
  useQuery<NextSurveySectionResponse>({
    queryKey: ['nextSurveySectionViewCreator', groupId],
    queryFn: () => new NextSurveySectionViewCreator().createView(groupId),
    // A survey in progress must never be refetched out from under the user. The drawer
    // records answers onto the section it was handed, so replacing that section mid-survey
    // silently discards them and the section submits blank. Refresh only when the drawer
    // explicitly asks (after a section is saved) — see useSurveyDrawerController, which
    // also keeps its own working copy so an imperative refetch can't clobber answers.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: groupId > 0,
  });
